import { useLazyQuery } from "@apollo/client/react";
import { useCallback } from "react";

import rethrowIfNotAborted from "apollo/rethrowIfNotAborted";
import { makeStringEqualsPredicate } from "utils/predicates";
import useFiltersVariables from "components/Filters/useFiltersVariables";

import {
  SEARCH_MANAGED_ENTITIES,
  SEARCH_STACK_MANAGED_ENTITIES,
  SearchManagedEntitiesData,
  SearchStackManagedEntitiesData,
} from "./gql";
import { IacManagementGroupBy } from "../types";
import { makeResourcesSortOption } from "./helpers";
import { FILTERS_TYPE, initialSortDirection, initialSortOption } from "../ListLayout/constants";

export type UseIacManagementResourcesFetchProps = {
  cursor: string | null;
  groupId: string;
  rowsPerPage: number;
  groupBy: IacManagementGroupBy;
  parentStackId?: string;
};

const useIacManagementResources = () => {
  const [accountSearchStackGroups, { loading: accountLoading }] = useLazyQuery(
    SEARCH_MANAGED_ENTITIES,
    {
      fetchPolicy: "network-only",
    }
  );

  const [stackSearchStackGroups, { loading: stackLoading }] = useLazyQuery(
    SEARCH_STACK_MANAGED_ENTITIES,
    {
      fetchPolicy: "network-only",
    }
  );

  const { filterVariables } = useFiltersVariables({
    filterType: FILTERS_TYPE,
    initialSortDirection,
    initialSortOption,
  });

  const fetchData = useCallback(
    async ({
      groupId,
      groupBy,
      parentStackId,
      cursor,
      rowsPerPage,
    }: UseIacManagementResourcesFetchProps) => {
      const groupByPredicate =
        groupBy === IacManagementGroupBy.None ? IacManagementGroupBy.Stack : groupBy;
      const predicatesPerGroup = [
        ...filterVariables.predicates,
        makeStringEqualsPredicate(groupByPredicate, [groupId]),
      ];

      // add the stack predicate for all queries in Stack view except for the Stack and None grouping options, to avoid duplicating it
      if (
        parentStackId &&
        groupBy !== IacManagementGroupBy.None &&
        groupBy !== IacManagementGroupBy.Stack
      ) {
        predicatesPerGroup.push(makeStringEqualsPredicate("stack", [parentStackId]));
      }

      const fetchFn = parentStackId ? stackSearchStackGroups : accountSearchStackGroups;

      let data;
      try {
        ({ data } = await fetchFn({
          variables: {
            ...{
              ...(parentStackId && { stackId: parentStackId }),
              input: {
                after: cursor,
                first: rowsPerPage,
                fullTextSearch: filterVariables.searchInput || null,
                orderBy: makeResourcesSortOption(filterVariables.sortOptionFields),
                predicates: predicatesPerGroup,
                requestedPage: null,
              },
            },
          },
        }));
      } catch (error) {
        rethrowIfNotAborted(error as Error);
        return { data: [], hasMore: false, endCursor: undefined };
      }

      const returnData = parentStackId
        ? (data as SearchStackManagedEntitiesData | undefined)?.stack?.searchManagedEntities
        : (data as SearchManagedEntitiesData | undefined)?.searchManagedEntities;

      return {
        data: returnData?.edges.map((edge) => edge.node) || [],
        hasMore: returnData?.pageInfo?.hasNextPage || false,
        endCursor: returnData?.pageInfo?.endCursor,
      };
    },
    [
      filterVariables.predicates,
      filterVariables.searchInput,
      filterVariables.sortOptionFields,
      stackSearchStackGroups,
      accountSearchStackGroups,
    ]
  );

  const fetchResourceById = useCallback(
    async (resourceId: string, parentStackId?: string) => {
      const predicatesPerGroup = [
        ...filterVariables.predicates,
        makeStringEqualsPredicate("id", [resourceId]),
      ];

      const fetchFn = parentStackId ? stackSearchStackGroups : accountSearchStackGroups;

      let data;
      try {
        ({ data } = await fetchFn({
          variables: {
            ...{
              input: {
                after: null,
                first: 1,
                fullTextSearch: null,
                orderBy: null,
                predicates: predicatesPerGroup,
                requestedPage: null,
              },
            },
          },
        }));
      } catch (error) {
        rethrowIfNotAborted(error as Error);
        return { data: undefined };
      }

      const returnData = parentStackId
        ? (data as SearchStackManagedEntitiesData | undefined)?.stack?.searchManagedEntities
        : (data as SearchManagedEntitiesData | undefined)?.searchManagedEntities;

      return {
        data: returnData?.edges.map((edge) => edge.node)[0] || undefined,
      };
    },
    [filterVariables.predicates, stackSearchStackGroups, accountSearchStackGroups]
  );

  return { fetchData, fetchResourceById, loading: accountLoading || stackLoading };
};

export default useIacManagementResources;
